import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Category } from '../../types';
import { useInventory } from '../../context/InventoryContext';
import { getDistinctColor } from '../../lib/colorService';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';

interface CategoryFormProps {
  category?: Category;
  isEdit?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ 
  category,
  isEdit = false
}) => {
  const navigate = useNavigate();
  const { categories, addCategory, updateCategory } = useInventory();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: getDistinctColor(categories),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (category && isEdit) {
      setFormData({
        name: category.name,
        description: category.description,
        color: category.color,
      });
    }
  }, [category, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.color.trim()) {
      newErrors.color = 'Color is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (isEdit && category) {
        await updateCategory({
          ...category,
          ...formData,
        });
      } else {
        await addCategory(formData);
      }
      
      navigate('/categories');
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{isEdit ? 'Edit Category' : 'Add New Category'}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <Input
            label="Category Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            error={errors.name}
            fullWidth
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="h-10 w-10 border-0 p-0"
              />
              <Input
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                error={errors.color}
                fullWidth
                required
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/categories')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
          >
            {isEdit ? 'Update Category' : 'Add Category'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default CategoryForm;